import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Play } from 'lucide-react';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { PremiumFooter } from '@/components/premium/PremiumFooter';
import { SEO } from '@/components/SEO';
import { PageFAQ } from '@/components/PageFAQ';
import { RelatedPages } from '@/components/RelatedPages';

const FACTORY_IMGS = [
  { src: "https://customer-assets.emergentagent.com/job_97f513fe-a3cb-403b-b669-1693d7f5acab/artifacts/xc04hkqg_Factory%201.png", alt: "Ezeefit Production Floor", label: "Production Floor" },
  { src: "https://customer-assets.emergentagent.com/job_97f513fe-a3cb-403b-b669-1693d7f5acab/artifacts/7lo3zof8_Factory%202.png", alt: "CNC Machining Facility", label: "CNC Machining" },
  { src: "https://customer-assets.emergentagent.com/job_97f513fe-a3cb-403b-b669-1693d7f5acab/artifacts/w54p034i_image.png", alt: "Quality Packing", label: "Quality Packing" },
];

const STOCK_IMGS = {
  officeRows: "https://images.unsplash.com/photo-1549637642-90187f64f420?w=900&q=75&auto=format",
  modernDesk: "https://images.unsplash.com/photo-1706689656095-168768dc20a5?w=900&q=75&auto=format",
  openPlan: "https://images.unsplash.com/photo-1718220216044-006f43e3a9b1?w=900&q=75&auto=format",
};

export default function Legacy() {
  const navigate = useNavigate();

  const handleProductionClick = () => {
    navigate('/home-premium', { state: { openProduction: true } });
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <SEO
        title="Our Legacy | Ezeefit Modular Furniture Pvt. Ltd. — Since 2014"
        description="From traditional carpentry to precision CNC machining — the story of how Ezeefit engineered modular office furniture for India."
        canonical="/legacy"
      />
      <PremiumHeader />

      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-center bg-[#1a1a1a] overflow-hidden">
        <div className="absolute inset-0">
          <img src={FACTORY_IMGS[0].src} alt="Ezeefit Production Floor" className="w-full h-full object-cover opacity-30" width={1200} height={800} fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a] via-[#1a1a1a]/90 to-transparent" />
        </div>
        <div className="relative z-10 container mx-auto px-4 sm:px-8 py-24 sm:py-32 max-w-3xl">
          <p className="text-[#c5a059] text-xs sm:text-sm font-bold tracking-[0.25em] uppercase mb-6">
            We were told the traditional offices could not be standardized.
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] mb-6">
            They Said It Couldn't Fit.
            <br />
            <span className="text-[#c5a059]">So We Engineered a New Legacy.</span>
          </h1>
          <p className="text-gray-300 text-base sm:text-lg leading-relaxed max-w-2xl">
            For decades, the world believed that modern, modular design belonged exclusively to non-traditional blueprints. They said our spaces were too unpredictable for flat-pack furniture. They were wrong. We didn't copy them. We engineered for India.
          </p>
        </div>
      </section>

      {/* INTRO — MFS x EZFPL Evolution */}
      <section className="bg-white py-16 sm:py-20 border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-8 max-w-4xl">
          <div className="flex items-baseline gap-4 mb-6">
            <span className="text-[#c5a059] text-sm font-bold tracking-[0.2em]">THE ORIGIN</span>
            <div className="h-px bg-[#c5a059] flex-1 mt-1" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1a1a1a] mb-6 uppercase tracking-tight">
            From EZFPL to MFS — A Decade of Evolution
          </h2>
          <div className="text-gray-600 text-sm sm:text-base leading-relaxed space-y-4">
            <p>
              In 2014, <strong className="text-[#1a1a1a]">Ezeefit Modular Furniture Pvt. Ltd. (EZFPL)</strong> was founded in Mumbai with a singular mission: to bring German-standard precision manufacturing to Indian office furniture. What started as a B2B manufacturing unit supplying modular panels and components to corporates across India, quickly grew into one of the most trusted names in the industry —
              <a href="https://www.ezeefitofficesystems.in" target="_blank" rel="noopener noreferrer" className="text-[#c5a059] hover:underline font-semibold"> www.ezeefitofficesystems.in</a>.
            </p>
            <p>
              Over a decade, EZFPL furnished <strong className="text-[#1a1a1a]">5,000+ offices</strong> for enterprises like L&T, P&G, Gillette, Ultratech, HPCL, and Awfis — earning a reputation for robust, no-nonsense, factory-direct office solutions built to last.
            </p>
            <p>
              But the market was changing. Small-to-mid-size companies, co-working spaces, and growing startups needed the same quality — without the bulk-order minimums and lengthy procurement cycles. They needed a way to <strong className="text-[#1a1a1a]">buy premium modular furniture online</strong>, one desk at a time, with transparent pricing and fast delivery.
            </p>
            <p>
              That's why EZFPL launched <strong className="text-[#1a1a1a]">Modular Furniture Store (MFS)</strong> — a direct-to-customer platform backed by the same production line, the same engineering team, and the same decade of manufacturing expertise. MFS isn't a startup. It's the public face of a legacy manufacturer, now accessible to everyone.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-6 text-center">
            <div className="flex-1 min-w-[140px] border-l-2 border-[#c5a059] pl-4 text-left">
              <p className="text-2xl sm:text-3xl font-extrabold text-[#1a1a1a]">2014</p>
              <p className="text-xs text-gray-500 mt-1">EZFPL Founded</p>
            </div>
            <div className="flex-1 min-w-[140px] border-l-2 border-[#c5a059] pl-4 text-left">
              <p className="text-2xl sm:text-3xl font-extrabold text-[#1a1a1a]">5,000+</p>
              <p className="text-xs text-gray-500 mt-1">Offices Furnished</p>
            </div>
            <div className="flex-1 min-w-[140px] border-l-2 border-[#c5a059] pl-4 text-left">
              <p className="text-2xl sm:text-3xl font-extrabold text-[#1a1a1a]">MFS</p>
              <p className="text-xs text-gray-500 mt-1">D2C Platform by EZFPL</p>
            </div>
            <div className="flex-1 min-w-[140px] border-l-2 border-[#c5a059] pl-4 text-left">
              <p className="text-2xl sm:text-3xl font-extrabold text-[#1a1a1a]">Pan India</p>
              <p className="text-xs text-gray-500 mt-1">Delivery Network</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 1 — THE IMPOSSIBLE BLUEPRINT */}
      <section className="bg-white py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-8 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <div className="flex items-baseline gap-4 mb-6">
                <span className="text-[#c5a059] text-sm font-bold tracking-[0.2em]">01</span>
                <div className="h-px bg-[#c5a059] flex-1 mt-1" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1a1a1a] mb-6 uppercase tracking-tight">
                The Impossible Blueprint
              </h2>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-4">
                When we set out to build ModularFurnitureStore.com, we didn't just want to sell boxes. We wanted to challenge a status quo that forced businesses to choose between chaotic, dust-filled months of on-site carpentry or cheap, imported furniture that warped at the first sign of a humid July.
              </p>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                We faced dozens of failed prototypes, broken hinges, and material delamination until we cracked the code. The result? A proprietary, moisture-sealed, ultra-load-bearing interlocking system engineered for Corporate Office life.
              </p>
            </div>
            <div className="relative">
              <img src={STOCK_IMGS.officeRows} alt="Modern modular office workstations in rows" className="w-full h-72 sm:h-80 object-cover" loading="lazy" width={900} height={600} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>
          </div>
          <blockquote className="border-l-4 border-[#c5a059] pl-6 my-10 py-2 max-w-3xl mx-auto">
            <p className="text-lg sm:text-xl text-[#1a1a1a] italic leading-relaxed">
              "A piece of office furniture shouldn't dictate how you work. It should expand to match the scale of your ambitions."
            </p>
          </blockquote>
        </div>
      </section>

      {/* SECTION 2 — THE UNTOLD HUMAN CRAFT */}
      <section className="bg-[#F8F6F1] py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-8 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Image first on this section for visual alternation */}
            <div className="order-2 lg:order-1 grid grid-cols-3 gap-2">
              {FACTORY_IMGS.map((img, i) => (
                <div key={i} className="relative overflow-hidden">
                  <img src={img.src} alt={img.alt} className="w-full h-40 sm:h-52 object-cover" loading="lazy" width={400} height={300} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <p className="absolute bottom-2 left-2 text-white text-[10px] sm:text-xs font-bold">{img.label}</p>
                </div>
              ))}
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-baseline gap-4 mb-6">
                <span className="text-[#c5a059] text-sm font-bold tracking-[0.2em]">02</span>
                <div className="h-px bg-[#c5a059] flex-1 mt-1" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1a1a1a] mb-6 uppercase tracking-tight">
                The Hosur of Furniture Making
              </h2>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-4">
                Greatness isn't born in marketing rooms. It's forged on the production floor, amid the scent of sawdust and the hum of precision machinery. We didn't outsource our soul.
              </p>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                Every modular panel, hidden bracket, and soft-close runner is crafted by a team of engineers and artisans who transitioned from traditional carpentry to advanced CNC machining. The hard-earned intuition of the Indian craftsman married to the ruthless accuracy of modern automation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — GENERATIONAL VALUE */}
      <section className="bg-white py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-8 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <div className="flex items-baseline gap-4 mb-6">
                <span className="text-[#c5a059] text-sm font-bold tracking-[0.2em]">03</span>
                <div className="h-px bg-[#c5a059] flex-1 mt-1" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1a1a1a] mb-6 uppercase tracking-tight">
                Built for the Next Generation
              </h2>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-6">
                In our culture, we don't buy things for the next two years. We buy them for the next twenty. Cheap fast-furniture cracks when you move and ends up in a landfill within a few seasons. We engineer with a Generational Design Language.
              </p>
              <div className="space-y-4">
                <div className="border-l-2 border-[#c5a059] pl-4">
                  <h3 className="text-sm font-extrabold text-[#1a1a1a] uppercase tracking-wider mb-1">Move-Proof Engineering</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">Heavy-duty locking joints — disassembled and rebuilt fifty times without losing their bite.</p>
                </div>
                <div className="border-l-2 border-[#c5a059] pl-4">
                  <h3 className="text-sm font-extrabold text-[#1a1a1a] uppercase tracking-wider mb-1">Scalable DNA</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">Start with a cluster of workstations. Snap on more modules seamlessly as your team grows.</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img src={STOCK_IMGS.modernDesk} alt="Modern office with plants and natural light" className="w-full h-72 sm:h-80 object-cover" loading="lazy" width={900} height={600} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
          </div>
          <p className="text-[#1a1a1a] text-base sm:text-lg font-bold mt-10 italic text-center max-w-2xl mx-auto">
            This isn't just modern furniture. This is an heirloom engineered for a modern, moving India.
          </p>
        </div>
      </section>

      {/* FULL-WIDTH IMAGE BREAK */}
      <div className="relative h-48 sm:h-64 overflow-hidden">
        <img src={STOCK_IMGS.openPlan} alt="Open-plan modern office space" className="w-full h-full object-cover" loading="lazy" width={1200} height={400} />
        <div className="absolute inset-0 bg-[#1a1a1a]/40" />
      </div>

      {/* FINAL CTA */}
      <section className="bg-[#1a1a1a] py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-8 max-w-3xl text-center">
          <p className="text-[#c5a059] text-xs font-bold tracking-[0.25em] uppercase mb-4">The Call to Action</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">
            Stop buying furniture for the Office you have today.
          </h2>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#c5a059] leading-tight mb-10">
            Start investing in the legacy you are building for tomorrow.
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/products">
              <Button className="bg-[#c5a059] hover:bg-[#b8934d] text-white rounded-none px-8 py-5 text-xs sm:text-sm tracking-[0.15em] font-bold" data-testid="legacy-explore-collection">
                EXPLORE OUR COLLECTION
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Button
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#1a1a1a] rounded-none px-8 py-5 text-xs sm:text-sm tracking-[0.15em] font-bold bg-transparent"
              data-testid="legacy-watch-production"
              onClick={handleProductionClick}
            >
              <Play className="w-4 h-4 mr-2" />
              WATCH OUR PRODUCTION STORY
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ + Interlinks */}
      <div className="container mx-auto px-4 sm:px-8 py-12 bg-white">
        <PageFAQ pageKey="legacy" />
        <RelatedPages pageKey="legacy" className="mt-12" />
      </div>

      <PremiumFooter />
    </div>
  );
}
